import { createContext, ReactNode, useCallback, useState } from "react";
import useLocalStorage from "use-local-storage";
import {
  cartAddItem,
  cartBaseTotal,
  cartEmpty,
  cartHasItems,
  cartItem,
  cartItemCount,
  cartItemRemove,
  cartItemUpdateAmount,
  cartSetItems,
  cartTotal,
  findCartItem,
  ICart,
  ICartItem,
  sampleCartItem
} from "../utils/CartUtils";
import {
  getProductBySku,
  productHasSamples,
  productHasStock
} from "../utils/ProductUtils";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../hooks/TranslationHooks";
import { useUiMessageContext } from "../components/UIMessages/context";
import { ERROR, WARN } from "../components/UIMessages";
import { gtmSendEvent, gtmSendUpdateCart } from "../utils/GTMUtils";

import { useProductCategories } from "../hooks/ProductCategoryHooks";
import { Product } from "../types/Product";
import { Asset } from "../types/Asset";

type CartContextProps = {
  getCartIsOpen: () => boolean;
  setCartDropdown: (open: boolean) => void;
  closeCartDropdown: () => void;
  toggleCartDropdown: () => void;
  getItems: () => ICartItem[];
  addToCart: (item: ICartItem) => void;
  loggedAddToCart: (
    product: Product,
    amount: number,
    dontExpandCart?: boolean,
    b2b?: boolean
  ) => boolean;
  loggedAddSampleToCart: (product: Product) => boolean;
  updateAmount: (index: number, amount: number) => void;
  removeFromCart: (index: number) => void;
  setItems: (items: ICartItem[]) => void;
  emptyCart: () => void;
  itemCount: () => number;
  hasItems: () => boolean;
  findItem: (sku: string) => ICartItem | undefined;
  total: (products: Product[]) => number;
  baseTotal: (products: Product[]) => number;
  refreshData: (products: Product[]) => void;
};

export const CartContext = createContext<CartContextProps>(
  {} as CartContextProps
);

const initialState: ICart = {
  items: []
};

const localStorageOptions = {
  serializer: (obj: unknown) => {
    return JSON.stringify(obj);
  },
  parser: (str: string) => {
    return JSON.parse(str);
  },
  logger: (error: unknown) => {
    console.error(error);
  }
};

export const CartContextProvider = ({ children }: { children: ReactNode }) => {
  const { displayMessage } = useUiMessageContext();
  const productCategories = useProductCategories();

  const [state, setState] = useLocalStorage<ICart>(
    "cart-context",
    initialState,
    localStorageOptions
  );
  const [cartIsOpen, setCartIsOpen] = useState(false);

  const t = useBjelinTranslations();
  const tp = useProductTranslations();
  if (state === undefined) {
    throw new Error("Cart state is undefined");
  }
  //console.debug("CartContextProvider render");

  const getCartIsOpen = useCallback(() => {
    return cartIsOpen;
  }, [cartIsOpen]);

  const setCartDropdown = useCallback((open: boolean) => {
    setCartIsOpen(open);
  }, []);

  const closeCartDropdown = useCallback(() => {
    setCartIsOpen(false);
  }, []);

  const toggleCartDropdown = useCallback(() => {
    setCartIsOpen(state => !state);
  }, []);

  const getItems = useCallback(() => {
    return state.items;
  }, [state]);

  const itemCount = useCallback(() => {
    return cartItemCount(state);
  }, [state]);

  const hasItems = useCallback(() => {
    return cartHasItems(state);
  }, [state]);

  const findItem = useCallback(
    (sku: string) => {
      return findCartItem(state, sku);
    },
    [state]
  );

  const addToCart = useCallback(
    (cartItem: ICartItem) => {
      setState(state => (state ? cartAddItem(state, cartItem) : undefined));
    },
    [setState]
  );

  const loggedAddToCart = useCallback(
    (product: Product, amount: number, dontExpandCart = false, b2b = false) => {
      if (amount <= 0) return false;

      const inCart = findItem(product.sku);
      if (!productHasStock(product, amount + (inCart?.amount || 0), b2b)) {
        displayMessage({
          type: ERROR,
          persist: true,
          code: "stock.insufficient.error",
          text: tp("stock.insufficient.error")
        });
        return false;
      }

      addToCart({ ...cartItem(product), amount });
      if (!dontExpandCart) {
        setCartIsOpen(true);
      }
      gtmSendUpdateCart(product.sku, product, 0, amount, productCategories);

      return true;
    },

    [addToCart, displayMessage, findItem, productCategories, tp]
  );

  const loggedAddSampleToCart = useCallback(
    (product: Product) => {
      if (!product.sampleSku) return false;
      const item = sampleCartItem(
        product as {
          name: string;
          sku: string;
          sampleSku: string;
          images?: Asset[];
        }
      );
      if (getItems().filter(({ sku }) => sku === item.sku).length === 0) {
        addToCart(item);
        gtmSendEvent({
          event: "bjelinAddSampleToCart",
          bjelinSampleData: {
            name: product.name,
            id: product.sku,
            currencyCode: product.price?.currency
          }
        });
        gtmSendUpdateCart(item.sku, product, 0, 1, productCategories);
      }
      setCartIsOpen(true);
      return true;
    },
    [addToCart, getItems, productCategories]
  );

  const updateAmount = useCallback(
    (index: number, amount: number) => {
      setState(state =>
        state ? cartItemUpdateAmount(state, index, amount) : undefined
      );
    },
    [setState]
  );

  const removeFromCart = useCallback(
    (index: number) => {
      setState(state => (state ? cartItemRemove(state, index) : undefined));
    },
    [setState]
  );

  const setItems = useCallback(
    (items: ICartItem[]) => {
      setState(state => (state ? cartSetItems(state, items) : undefined));
    },
    [setState]
  );

  const emptyCart = useCallback(() => {
    setState(state => (state ? cartEmpty(state) : undefined));
  }, [setState]);

  const total = useCallback(
    (products: Product[]) => {
      return cartTotal(state, products);
    },
    [state]
  );

  const baseTotal = useCallback(
    (products: Product[]) => {
      return cartBaseTotal(state, products);
    },
    [state]
  );

  const refreshData = useCallback(
    (products: Product[]) => {
      setState(state => {
        console.log("CartContextProvider::refreshData", state);
        if (state === undefined) return;
        const items = state.items;
        const newItems = items.reduce((items: ICartItem[], item) => {
          const product = getProductBySku(products, item.realSku || item.sku);
          if (!product) {
            displayMessage({
              type: WARN,
              persist: true,
              code: "cart.unknown_item_removed",
              text: t("cart.unknown_item_removed", { item: item.name })
            });
            return items;
          }
          /* Removed until we can do the check after user specific price list has loaded
        if (!item.isSample && !product?.price?.basePrice) {
          displayMessage({
            type: WARN,
            persist: true,
            code: "cart.unpurchaseable_item_removed",
            text: t("cart.unpurchaseable_item_removed", { item: item.name })
          });
          return items;
        }*/
          if (
            item.isSample &&
            !(productHasSamples(product) && product.hasSampleInStock)
          ) {
            displayMessage({
              type: WARN,
              persist: true,
              code: "cart.sample_item_removed",
              text: t("cart.sample_item_removed", { item: product.name })
            });
            return items;
          }
          const newItem = item.isSample
            ? sampleCartItem(
                product as {
                  name: string;
                  sku: string;
                  sampleSku: string;
                  images?: Asset[];
                }
              )
            : cartItem(product);
          newItem.amount = item.amount;
          items.push(newItem);
          return items;
        }, []);
        return { ...state, items: newItems };
      });
    },
    [setState, displayMessage, t]
  );

  return (
    <CartContext.Provider
      value={{
        getItems,
        addToCart,
        loggedAddToCart,
        loggedAddSampleToCart,
        updateAmount,
        removeFromCart,
        setItems,
        emptyCart,
        refreshData,
        itemCount,
        hasItems,
        findItem,
        total,
        baseTotal,
        getCartIsOpen,
        setCartDropdown,
        closeCartDropdown,
        toggleCartDropdown
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
